import React from 'react'
import { LogoLogomarkColorDefaultIcon, Flex, Icon, Text } from '@grammarly/design-system'

import {
  AiDetectorIcon,
  AiHumanizerIcon,
  PlagiarismCheckerIcon,
  ParaphrasingToolIcon,
  GrammarCheckIcon,
} from '@components/tool/icons'
import { AIDetector } from './ai-detector'
import { PlagiarismChecker } from './plagiarism-checker'
import { GoTemplates } from './go-templates'
import { useTool } from '../tool-context'
import { ToolCopyButton } from './tool-copy-button'

import styles from '../tool.module.scss'
import { ToolNextBestAction } from './tool-next-best-action'

const ToolOutputIcon: React.FC = () => {
  const { toolType } = useTool()

  // The nav icons are different from the GDS icons. Will temporarily import from the secondary nav.
  switch (toolType) {
    case 'aiDetector':
      return <AiDetectorIcon />
    case 'plagiarismChecker':
      return <PlagiarismCheckerIcon />
    case 'paraphraser':
      return <ParaphrasingToolIcon />
    case 'humanizer':
      return <AiHumanizerIcon />
    case 'grammarChecker':
      return <GrammarCheckIcon />
    default:
      return <Icon icon={LogoLogomarkColorDefaultIcon} accessibilityLabel="" />
  }
}

export const ToolOutput: React.FC = () => {
  const { outputTitle } = useTool()

  return (
    <Flex direction="column" className={styles.toolOutputContainer}>
      <Flex
        direction="column"
        paddingTop={4}
        paddingBottom={4}
        paddingLeft={6}
        paddingRight={6}
        className={styles.toolOutputTitleRowContainer}
      >
        <Flex direction="row" justify="space-between" align="center" padding={0} gap={2}>
          <Flex direction="row" align="center" gap={2}>
            <ToolOutputIcon />
            <Text variant="heading-small" as="span" color="base-subdued">
              {outputTitle}
            </Text>
          </Flex>
          <ToolCopyButton />
        </Flex>
      </Flex>
      <Flex
        direction="column"
        paddingTop={0}
        paddingBottom={0}
        paddingLeft={6}
        paddingRight={6}
        gap={2}
        className={styles.toolOutputContentContainer}
      >
        <ToolOutputContent />
      </Flex>
      <ToolNextBestAction />
    </Flex>
  )
}

const ToolOutputContent: React.FC = () => {
  const { toolType, isLoading, outputContent } = useTool()

  switch (toolType) {
    case 'aiDetector':
      return <AIDetector isLoading={isLoading} outputContent={outputContent} />
    case 'plagiarismChecker':
      return <PlagiarismChecker isLoading={isLoading} outputContent={outputContent} />
    case 'paraphraser':
    case 'humanizer':
    case 'goTemplatesGeneric':
      return <GoTemplates isLoading={isLoading} outputContent={outputContent} />
    default:
      return null
  }
}
